import * as React from 'react';

import { observer, inject } from 'mobx-react';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { FurtherConsiderationsEntryStore } from '../stores/further-considerations-entry-store';
import { Container, Button, Form, Col, Card, Row } from 'react-bootstrap';
import { ClientEntryStore } from '../stores/client-entry-store';
import { Bloodwork, Client } from '../models';
import { BloodworkDetailsModal } from '../components/bloodworkz/bloodwork-details-modal';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';
import { BloodworkDisplay } from '../components/bloodworkz/bloodwork-display';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UserStore } from '../stores/user-store';
import { BloodworkComparisonAnalysis } from '../components/bloodworkz/bloodwork-comparison-analysis';
import Select from 'react-select';

interface BloodworkAnalysisContainerProps {
  userStore?: UserStore;
  userProfileUIStore?: UserProfileUIStore;
  bloodworkEntryStore?: BloodworkEntryStore;
  clientEntryStore?: ClientEntryStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
  history?: any;
}

type State = {
  clientId: string;
  selectedClient: Client;
  selectedBloodwork: Bloodwork;
  bloodwork: Bloodwork;
  showBloodworkDetailsModal: boolean;
  showClientBloodworks: boolean;
  scrollToComparison: boolean;
};

function ClientOption(props: any) {
  const { data, isSelected, isFocused, innerRef, innerProps, options } = props;
  const index = (options as any[]).findIndex((o: any) => o.value === data.value);
  let bg = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
  if (isFocused) bg = '#e8f0fe';
  if (isSelected) bg = '#0d6efd';
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        backgroundColor: bg,
        color: isSelected ? '#ffffff' : '#212529',
        padding: '3px 12px',
        fontSize: '13px',
        borderBottom: '1px solid #eeeeee',
        cursor: 'pointer',
        fontWeight: isSelected ? 600 : 400,
      }}
    >
      {data.label}
    </div>
  );
}

@inject(
  'userStore',
  'userProfileUIStore',
  'bloodworkEntryStore',
  'clientEntryStore',
  'furtherConsiderationsEntryStore',
)
@observer
export class BloodworkAnalysisContainer extends React.Component<
  BloodworkAnalysisContainerProps,
  {}
> {
  state: State = {
    clientId: '',
    selectedClient: undefined,
    selectedBloodwork: undefined,
    bloodwork: undefined,
    showBloodworkDetailsModal: false,
    showClientBloodworks: true,
    scrollToComparison: false,
  };

  componentWillMount() {
    const {
      clientEntryStore,
      furtherConsiderationsEntryStore,
      bloodworkEntryStore,
      userProfileUIStore,
      history,
    } = this.props;
    bloodworkEntryStore.loadBloodworkRanges();

    const routeState =
      history && history.location && history.location.state
        ? history.location.state
        : null;
    const stateClientId = routeState ? routeState.clientId : null;
    const scrollToComparison = routeState
      ? !!routeState.scrollToComparison
      : false;

    clientEntryStore.loadCurrentClients().then(() => {
      if (stateClientId) {
        this.setState({ clientId: stateClientId, scrollToComparison }, () => {
          this.loadResults();
        });
      }
    });

    furtherConsiderationsEntryStore.loadFurtherConsiderations();
    userProfileUIStore.loadCurrentUserProfile();
  }

  componentWillUnmount() {
    const { bloodworkEntryStore } = this.props;
    bloodworkEntryStore.resetClientBloodworks();
  }

  handleClientChange = (opt: any) => {
    const { bloodworkEntryStore } = this.props;
    const clientId = opt?.value ?? '';

    bloodworkEntryStore.resetClientBloodworks();

    this.setState({
      clientId: clientId,
      selectedClient: undefined,
      selectedBloodwork: undefined,
    });
  };

  loadResults() {
    const { clientEntryStore, bloodworkEntryStore } = this.props;
    const { clientId } = this.state;
    const selectedClient = Client.findClient(
      clientId,
      clientEntryStore.clients,
    );
    bloodworkEntryStore.resetCurrentBloodwork();

    this.setState({
      selectedClient,
    });

    if (selectedClient) {
      bloodworkEntryStore.loadClientBloodworks(selectedClient.id).then(() => {
        if (this.state.scrollToComparison) {
          // Fire once per navigation from the Home "Analyze Results Table"
          // button, then clear the flag so on-page client changes don't scroll.
          this.setState({ scrollToComparison: false });
          this.scrollToComparison();
        }
      });
    }
  }

  // Scroll the "Test Results Comparison" card to the top of the viewport once the
  // client's bloodworks have rendered. Mirrors the sticky-offset + retry pattern
  // used by scrollToAnalyzeTestResults in bloodwork-test-analysis.tsx.
  scrollToComparison() {
    const scrollToCard = () => {
      const element = document.getElementById('blood-work-comparison');
      if (!element) return false;
      const sticky = document.querySelector(
        '.App-sticky-top',
      ) as HTMLElement | null;
      const stickyHeight = sticky
        ? sticky.getBoundingClientRect().height
        : 0;
      const top =
        element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top });
      return true;
    };
    if (!scrollToCard()) {
      // The comparison table renders asynchronously after bloodworks load;
      // retry once if the anchor isn't in the DOM yet.
      setTimeout(scrollToCard, 1000);
    }
  }

  selectClient() {
    const { clientEntryStore } = this.props;
    if (clientEntryStore.clients === undefined) {
      return null;
    }

    const sorted = clientEntryStore.clients
      .slice()
      .sort((a: Client, b: Client) =>
        (a.lastName || '').localeCompare(b.lastName || ''),
      );

    const options = sorted.map((c: Client) => ({
      value: c.id,
      label: `${c.lastName}, ${c.firstName}`,
    }));

    const selectedValue =
      options.find((o) => o.value === this.state.clientId) ?? null;

    return (
      <Card>
        <Card.Header>Select Client</Card.Header>
        <Card.Body>
          <Form noValidate>
            <Form.Group as={Row} controlId="clientGroup">
              <Form.Label column sm={1}>
                Client Name
              </Form.Label>
              <Col sm={3} lg={3}>
                <Select
                  placeholder="Search by name..."
                  isSearchable
                  options={options}
                  value={selectedValue}
                  onChange={(opt: any) => this.handleClientChange(opt)}
                  maxMenuHeight={500}
                  components={{ Option: ClientOption }}
                  styles={{
                    control: (base: any, state: any) => ({
                      ...base,
                      borderColor: state.isFocused ? '#80bdff' : '#ced4da',
                      boxShadow: state.isFocused
                        ? '0 0 0 0.2rem rgba(0,123,255,.25)'
                        : 'none',
                      '&:hover': { borderColor: '#ced4da' },
                      fontSize: '14px',
                      minHeight: '38px',
                    }),
                    menu: (base: any) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    placeholder: (base: any) => ({
                      ...base,
                      color: '#6c757d',
                      fontSize: '14px',
                    }),
                    singleValue: (base: any) => ({
                      ...base,
                      fontSize: '12px',
                      fontWeight: 600,
                    }),
                  }}
                />
              </Col>
              <Col>
                <Button
                  type="button"
                  onClick={() => {
                    this.loadResults();
                  }}
                >
                  <FontAwesomeIcon icon="table" />
                  &nbsp;&nbsp;Show Test Results
                </Button>
              </Col>
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>
    );
  }

  viewResults(bloodwork: Bloodwork) {
    this.setState({
      bloodwork,
      selectedBloodwork: undefined,
      showBloodworkDetailsModal: true,
    });
  }

  onHideBloodworkDetails() {
    this.setState({
      showBloodworkDetailsModal: false,
    });
  }

  deleteBloodwork(bloodwork: Bloodwork) {
    const deleteBloodwork = window.confirm(
      'Are you sure you want to delete the test results?',
    );
    if (deleteBloodwork) {
      const { bloodworkEntryStore } = this.props;
      bloodworkEntryStore.deleteBloodwork(bloodwork);
      this.loadResults();
    }
  }
  onExportToPdf() {
    this.setState({
      showClientBloodworks: false,
    });
  }

  onHidePdfModal() {
    this.setState({
      showClientBloodworks: true,
    });
  }

  render() {
    const { selectedClient, showClientBloodworks } = this.state;
    const {
      userProfileUIStore,
      bloodworkEntryStore,
      furtherConsiderationsEntryStore,
      clientEntryStore,
    } = this.props;
    const clientBloodworks = bloodworkEntryStore.clientBloodworks;

    // NOTE: Using the state variable showClientBloodworks to fix an issue with PDF exports
    // when there are several client bloodworks displayed.
    return (
      <Container fluid>
        {this.selectClient()}

        {clientBloodworks.length > 0 && showClientBloodworks ? (
          <>
            <BloodworkDisplay
              history={this.props.history}
              currentUserProfile={userProfileUIStore.currentUserProfile}
              testResultsPanelLabel="Client Test Results"
              showPreviousOnCharts={true}
              bloodworks={clientBloodworks}
              bloodworkEntryStore={bloodworkEntryStore}
              refreshBloodworks={bloodworkEntryStore.refreshBloodworks}
              loadClientBloodworks={bloodworkEntryStore.loadClientBloodworks}
              furtherConsiderations={
                furtherConsiderationsEntryStore.furtherConsiderations
              }
              allowLoadMoreTestResults={false}
              updateClient={(client) => clientEntryStore.updateClientFields(client)}
              currentClients={clientEntryStore.clients}
            />
          </>
        ) : selectedClient ? (
          <div>
            <br />
            &nbsp;&nbsp;No test results found.
          </div>
        ) : null}

        <BloodworkComparisonAnalysis
          bloodworks={clientBloodworks}
          bloodworkRanges={bloodworkEntryStore.bloodworkRanges}
          currentUserProfile={userProfileUIStore.currentUserProfile}
          onExportToPdf={() => this.onExportToPdf()}
          onHidePdfModal={() => this.onHidePdfModal()}
          bloodworkEntryStore={bloodworkEntryStore}
          clientEntryStore={clientEntryStore}
          furtherConsiderationsEntryStore={furtherConsiderationsEntryStore}
          userProfileUIStore={userProfileUIStore}
        />

        <BloodworkDetailsModal
          show={this.state.showBloodworkDetailsModal}
          bloodwork={this.state.bloodwork}
          bloodworkRanges={bloodworkEntryStore.bloodworkRanges}
          onHide={() => {
            this.onHideBloodworkDetails();
          }}
        />
      </Container>
    );
  }
}
